import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { BrandMark, useBranding, loginBackground } from "../ujms/branding";


const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const nextRoute = new URLSearchParams(useLocation().search).get("next");
  const brand = useBranding();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [keep, setKeep] = useState(false);
  const [err, setErr] = useState("");
  const [help, setHelp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const next = (e) => {
    e.preventDefault();
    if (!username.trim()) { setErr("This field cannot be left blank"); return; }
    setErr("");
    setStep(2);
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!password) { setErr("This field cannot be left blank"); return; }
    setErr("");
    setSubmitting(true);
    const res = await login(username.trim(), password);
    if (res.success) {
      toast.success(`Signed in as ${res.user.name}`);
      navigate("/start" + (nextRoute ? `?next=${encodeURIComponent(nextRoute)}` : ""));
    } else {
      setErr(res.error || "Sign in failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-10"
      style={{ background: loginBackground(brand) }}
      data-testid="login-page">
      <div className="w-[440px] max-w-[92vw] bg-white rounded-md shadow-2xl border border-slate-200/70 overflow-hidden" data-testid="login-card">
        {/* logo header */}
        <div className="flex items-center justify-center gap-3 py-7 px-6 bg-[#263238] border-b-2 border-[#46d3b1]" data-testid="login-ujms-logo">
          <BrandMark className="h-12 w-12 drop-shadow" data-testid="login-court-seal" />
          <div className="leading-none max-w-[240px]">
            <span className="font-heading text-[30px] leading-tight font-bold text-white block" data-testid="login-court-name">{brand.court_name}</span>
            <div className="text-[11px] font-medium text-slate-300 mt-2" data-testid="login-product-name">Your court workspace · Powered by CJMS</div>
          </div>
        </div>

        <div className="px-7 sm:px-10 py-7">
          <h1 className="text-center text-[24px] font-heading font-bold text-slate-800 mb-8" data-testid="login-title">Sign In</h1>

          {step === 1 ? (
            <form onSubmit={next} noValidate>
              <div className="text-[17px] font-semibold text-slate-800">Username</div>
              <div className="text-[15px] text-slate-400 font-medium mt-0.5">Court Email or Username</div>
              <input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus
                className={`mt-3 w-full h-12 border px-3 text-[15px] font-medium text-slate-800 rounded-[3px] transition-colors focus:outline-none focus:border-ujms focus:ring-2 focus:ring-ujms/15 ${err ? "border-red-500" : "border-slate-300"}`}
                data-testid="login-username-input" name="username" />
              {err && (
                <div className="flex items-center gap-1.5 mt-2 text-[15px] font-medium text-[#d93025]" data-testid="login-error">
                  <CircleAlert className="w-4 h-4 fill-[#d93025] text-white" /> {err}
                </div>
              )}
              <label className="flex items-center gap-2.5 mt-6 text-[15px] font-medium text-slate-700 cursor-pointer">
                <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)}
                  className="w-4 h-4 border-slate-400" data-testid="login-keep-signed-in" />
                Keep me signed in
              </label>
              <button type="submit" data-testid="login-next-btn"
                className="mt-7 w-full h-[52px] bg-gradient-to-r from-ujms-dark to-ujms-light hover:from-ujms-deep hover:to-ujms text-white text-[16px] font-bold rounded-[3px] shadow-sm transition-all duration-150 active:scale-[0.99]">
                Next
              </button>
            </form>
          ) : (
            <form onSubmit={signIn} noValidate>
              <div className="text-[15px] font-medium text-slate-500 mb-4" data-testid="login-signing-in-as">
                Signing in as <b className="text-slate-800">{username}</b>{" "}
                <button type="button" className="text-ujms underline ml-1" onClick={() => { setStep(1); setPassword(""); setErr(""); }}
                  data-testid="login-back-btn">Change</button>
              </div>
              <div className="text-[17px] font-semibold text-slate-800">Password</div>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
                className={`mt-3 w-full h-12 border px-3 text-[15px] font-medium text-slate-800 rounded-[3px] transition-colors focus:outline-none focus:border-ujms focus:ring-2 focus:ring-ujms/15 ${err ? "border-red-500" : "border-slate-300"}`}
                data-testid="login-password-input" name="password" />
              {err && (
                <div className="flex items-center gap-1.5 mt-2 text-[15px] font-medium text-[#d93025]" data-testid="login-error">
                  <CircleAlert className="w-4 h-4 fill-[#d93025] text-white" /> {err}
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="login-submit-btn"
                className="mt-7 w-full h-[52px] bg-gradient-to-r from-ujms-dark to-ujms-light hover:from-ujms-deep hover:to-ujms text-white text-[16px] font-bold rounded-[3px] shadow-sm transition-all duration-150 active:scale-[0.99] disabled:opacity-60">
                {submitting ? "Signing In…" : "Sign In"}
              </button>
            </form>
          )}

          <div className="mt-8 space-y-3">
            <button type="button" className="block text-[15px] font-medium text-slate-700 hover:text-ujms"
              onClick={() => toast.info("Ask your Court Administrator to unlock your account under Administration › Sign-in security.")}
              data-testid="login-unlock-link">Unlock account?</button>
            <button type="button" className="block text-[15px] font-medium text-slate-700 hover:text-ujms"
              onClick={() => setHelp((v) => !v)} data-testid="login-help-link">Help</button>
            <a href="/portal" target="_blank" rel="noopener noreferrer"
              className="block text-[15px] font-medium text-ujms hover:underline"
              data-testid="login-public-portal-link">Public Case Records Portal</a>
            <a href="/home" className="block text-[15px] font-medium text-ujms hover:underline"
              data-testid="login-about-link">About CJMS · Start a 30-day trial</a>
          </div>

          {help && (
            <div className="mt-4 border border-slate-200 rounded-sm p-3 text-[12px] text-slate-600 space-y-1.5" data-testid="login-help-panel">
              <div className="font-semibold">Signing in</div>
              <div>Court staff sign in with the username issued by their Court Administrator. Trial workspace administrators sign in with their work email and the temporary password sent by RB Technologies, then change it from their profile.</div>
              <div>Five wrong passwords lock a username for 15 minutes; a Court Administrator can unlock it under Administration › Sign-in security.</div>
              <a href="mailto:melgiribosso@gmail.com?subject=CJMS%20sign-in%20help" className="inline-block font-semibold text-ujms hover:underline" data-testid="login-support-link">Contact RB Technologies support</a>
            </div>
          )}
        </div>
      </div>
      <div className="absolute bottom-3 left-0 right-0 text-center text-[11px] font-medium text-white/90 drop-shadow" data-testid="login-footer">
        CJMS · Justice management · © 2026 RB Technologies
      </div>
    </div>
  );
};

export default Login;
